import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ArrowLeft, 
  Search, 
  UserPlus, 
  Phone, 
  Calendar,
  Package,
  Loader2,
  User,
  Clock,
  RefreshCw,
  ChevronRight,
  AlertCircle,
  Check
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ClientsPage = ({ onBack, onSelectClient }) => {
  const { language } = useLanguage();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showFollowUpDialog, setShowFollowUpDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [creating, setCreating] = useState(false);
  const [followUpsToday, setFollowUpsToday] = useState({ scheduled_follow_ups: [], renewal_reminders: [] });

  const [newClient, setNewClient] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    skin_type: '',
    notes: ''
  });

  const [followUpData, setFollowUpData] = useState({
    follow_up_date: '',
    follow_up_reason: 'check_up',
    notes: ''
  });

  useEffect(() => {
    fetchClients();
    fetchFollowUps();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFollowUps = async () => {
    try {
      const response = await axios.get(`${API_URL}/clients/to-follow-up`);
      setFollowUpsToday(response.data);
    } catch (error) {
      console.error('Failed to fetch follow-ups:', error);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      fetchClients();
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/clients?search=${encodeURIComponent(searchQuery)}`);
      setClients(response.data);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async () => {
    if (!newClient.first_name || !newClient.last_name || !newClient.phone) {
      toast.error(language === 'fr' ? 'Remplissez les champs obligatoires' : 'Fill required fields');
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API_URL}/clients`, newClient);
      toast.success(language === 'fr' ? 'Client créé!' : 'Client created!');
      setShowCreateDialog(false);
      setNewClient({ first_name: '', last_name: '', phone: '', skin_type: '', notes: '' });
      fetchClients();
      
      // If onSelectClient is provided, select the new client
      if (onSelectClient) {
        onSelectClient(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setCreating(false);
    }
  };

  const handleScheduleFollowUp = async () => {
    if (!followUpData.follow_up_date || !selectedClient) return;

    try {
      await axios.post(`${API_URL}/clients/${selectedClient.id}/follow-up`, followUpData);
      toast.success(language === 'fr' ? 'RDV programmé!' : 'Follow-up scheduled!');
      setShowFollowUpDialog(false);
      setSelectedClient(null);
      setFollowUpData({ follow_up_date: '', follow_up_reason: 'check_up', notes: '' });
      fetchClients();
      fetchFollowUps();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleMarkFollowUpDone = async (clientId) => {
    try {
      await axios.delete(`${API_URL}/clients/${clientId}/follow-up`);
      toast.success(language === 'fr' ? 'Suivi effectué!' : 'Follow-up done!');
      fetchFollowUps();
      fetchClients();
    } catch (error) {
      toast.error('Error');
    }
  };

  const openFollowUpDialog = (client) => {
    setSelectedClient(client);
    setFollowUpData({
      follow_up_date: '',
      follow_up_reason: 'check_up',
      notes: ''
    });
    setShowFollowUpDialog(true);
  };

  const getSkinTypeLabel = (type) => {
    const labels = {
      'dry': language === 'fr' ? 'Sèche' : 'Dry',
      'oily': language === 'fr' ? 'Grasse' : 'Oily',
      'combination': language === 'fr' ? 'Mixte' : 'Combination',
      'normal': language === 'fr' ? 'Normale' : 'Normal',
      'sensitive': language === 'fr' ? 'Sensible' : 'Sensitive'
    };
    return labels[type] || type || '-';
  };

  const totalFollowUps = (followUpsToday.scheduled_follow_ups?.length || 0) + 
                         (followUpsToday.renewal_reminders?.length || 0);

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-[#E7E5E4] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={onBack}
                className="text-[#78716C] hover:text-[#0F172A]"
                data-testid="clients-back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-lg font-semibold text-[#0F172A]">
                  {language === 'fr' ? 'Clients' : 'Clients'}
                </h1>
                <p className="text-sm text-[#78716C]">
                  {clients.length} {language === 'fr' ? 'clients enregistrés' : 'registered clients'}
                </p>
              </div>
            </div>
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white"
              data-testid="create-client-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Nouveau' : 'New'}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Follow-ups Alert */}
        {totalFollowUps > 0 && (
          <Card className="border-orange-300 bg-orange-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-orange-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-orange-800">
                    {totalFollowUps} {language === 'fr' ? 'client(s) à relancer' : 'client(s) to follow up'}
                  </p>
                  <p className="text-sm text-orange-600">
                    {followUpsToday.scheduled_follow_ups?.length || 0} RDV + {followUpsToday.renewal_reminders?.length || 0} {language === 'fr' ? 'renouvellements' : 'renewals'}
                  </p>
                </div>
              </div>
              
              {/* Quick follow-up list */}
              <div className="mt-3 space-y-2">
                {followUpsToday.scheduled_follow_ups?.slice(0, 3).map(client => (
                  <div key={client.id} className="flex items-center justify-between bg-white rounded-lg p-2">
                    <div>
                      <p className="font-medium text-sm">{client.first_name} {client.last_name}</p>
                      <p className="text-xs text-[#78716C]">{client.phone}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleMarkFollowUpDone(client.id)}
                      className="text-green-600 border-green-300"
                    >
                      <Check className="w-4 h-4 mr-1" />
                      {language === 'fr' ? 'Fait' : 'Done'}
                    </Button>
                  </div>
                ))}
                {followUpsToday.renewal_reminders?.slice(0, 2).map(client => (
                  <div key={client.id} className="flex items-center justify-between bg-white rounded-lg p-2">
                    <div>
                      <p className="font-medium text-sm">{client.first_name} {client.last_name}</p>
                      <p className="text-xs text-orange-600 flex items-center gap-1">
                        <RefreshCw className="w-3 h-3" />
                        {language === 'fr' ? 'Renouvellement' : 'Renewal'}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openFollowUpDialog(client)}
                    >
                      <Calendar className="w-4 h-4 mr-1" />
                      RDV
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#78716C]" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder={language === 'fr' ? 'Rechercher par nom ou téléphone...' : 'Search by name or phone...'}
              className="pl-10"
              data-testid="client-search"
            />
          </div>
          <Button onClick={handleSearch} variant="outline">
            <Search className="w-4 h-4" />
          </Button>
        </div>

        {/* Clients List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#0EA5E9]" />
          </div>
        ) : clients.length === 0 ? (
          <div className="text-center py-12">
            <User className="w-12 h-12 text-[#E7E5E4] mx-auto mb-4" />
            <p className="text-[#78716C]">
              {language === 'fr' ? 'Aucun client trouvé' : 'No clients found'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {clients.map(client => (
              <Card 
                key={client.id} 
                className="border-[#E7E5E4] hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => onSelectClient && onSelectClient(client)}
                data-testid={`client-card-${client.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#E0F2FE] flex items-center justify-center">
                      <User className="w-6 h-6 text-[#0EA5E9]" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-[#0F172A]">
                          {client.first_name} {client.last_name}
                        </p>
                        {client.follow_up_date && (
                          <Badge className="bg-orange-100 text-orange-700 text-xs">
                            <Clock className="w-3 h-3 mr-1" />
                            {client.follow_up_date}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-sm text-[#78716C] mt-1">
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {client.phone}
                        </span>
                        {client.skin_type && (
                          <Badge variant="outline" className="text-xs">
                            {getSkinTypeLabel(client.skin_type)}
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <p className="text-sm font-medium text-[#0F172A]">
                        {client.total_purchases} {language === 'fr' ? 'achats' : 'purchases'}
                      </p>
                      <p className="text-xs text-[#78716C]">
                        {client.total_spent?.toLocaleString('fr-FR')} FCFA
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => { e.stopPropagation(); openFollowUpDialog(client); }}
                        data-testid={`schedule-followup-${client.id}`}
                      >
                        <Calendar className="w-4 h-4" />
                      </Button>
                      <ChevronRight className="w-5 h-5 text-[#78716C]" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Create Client Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#0EA5E9]" />
              {language === 'fr' ? 'Nouveau client' : 'New Client'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Prénom' : 'First Name'} *</Label>
                <Input
                  value={newClient.first_name}
                  onChange={(e) => setNewClient({ ...newClient, first_name: e.target.value })}
                  placeholder="Fatou"
                  data-testid="client-firstname"
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Nom' : 'Last Name'} *</Label>
                <Input
                  value={newClient.last_name}
                  onChange={(e) => setNewClient({ ...newClient, last_name: e.target.value })}
                  placeholder="Diallo"
                  data-testid="client-lastname"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Téléphone' : 'Phone'} *</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#78716C]" />
                <Input
                  value={newClient.phone}
                  onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                  placeholder="07 12 34 56 78"
                  className="pl-10"
                  data-testid="client-phone"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Type de peau' : 'Skin Type'}</Label>
              <Select
                value={newClient.skin_type}
                onValueChange={(v) => setNewClient({ ...newClient, skin_type: v })}
              >
                <SelectTrigger data-testid="client-skintype">
                  <SelectValue placeholder={language === 'fr' ? 'Sélectionner...' : 'Select...'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">{language === 'fr' ? 'Normale' : 'Normal'}</SelectItem>
                  <SelectItem value="dry">{language === 'fr' ? 'Sèche' : 'Dry'}</SelectItem>
                  <SelectItem value="oily">{language === 'fr' ? 'Grasse' : 'Oily'}</SelectItem>
                  <SelectItem value="combination">{language === 'fr' ? 'Mixte' : 'Combination'}</SelectItem>
                  <SelectItem value="sensitive">{language === 'fr' ? 'Sensible' : 'Sensitive'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Notes' : 'Notes'}</Label>
              <Input
                value={newClient.notes}
                onChange={(e) => setNewClient({ ...newClient, notes: e.target.value })}
                placeholder={language === 'fr' ? 'Allergies, préférences...' : 'Allergies, preferences...'}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button
              onClick={handleCreateClient}
              disabled={creating}
              className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white"
            >
              {creating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  {language === 'fr' ? 'Créer' : 'Create'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Follow-up Dialog */}
      <Dialog open={showFollowUpDialog} onOpenChange={setShowFollowUpDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#0EA5E9]" />
              {language === 'fr' ? 'Programmer un suivi' : 'Schedule Follow-up'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedClient && (
            <div className="space-y-4">
              <div className="p-3 bg-[#F5F5F4] rounded-lg">
                <p className="font-medium">{selectedClient.first_name} {selectedClient.last_name}</p>
                <p className="text-sm text-[#78716C]">{selectedClient.phone}</p>
              </div>
              
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Date du RDV' : 'Follow-up Date'}</Label>
                <Input
                  type="date"
                  value={followUpData.follow_up_date}
                  onChange={(e) => setFollowUpData({ ...followUpData, follow_up_date: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  data-testid="followup-date"
                />
              </div>
              
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Motif' : 'Reason'}</Label>
                <Select
                  value={followUpData.follow_up_reason}
                  onValueChange={(v) => setFollowUpData({ ...followUpData, follow_up_reason: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="check_up">{language === 'fr' ? 'Suivi routine' : 'Routine Check'}</SelectItem>
                    <SelectItem value="renewal">{language === 'fr' ? 'Renouvellement produits' : 'Product Renewal'}</SelectItem>
                    <SelectItem value="custom">{language === 'fr' ? 'Autre' : 'Other'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Notes' : 'Notes'}</Label>
                <Input
                  value={followUpData.notes}
                  onChange={(e) => setFollowUpData({ ...followUpData, notes: e.target.value })}
                  placeholder={language === 'fr' ? 'Notes optionnelles...' : 'Optional notes...'}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFollowUpDialog(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button
              onClick={handleScheduleFollowUp}
              disabled={!followUpData.follow_up_date}
              className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white"
            >
              <Calendar className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Programmer' : 'Schedule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientsPage;
